import { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

interface FlightPathProperties {
  drone_id: string;
  flight_id: string;
  start_time: string;
  end_time: string;
  points_count: number;
  flight_distance: number;
  max_height: number;
  max_speed: number;
  avg_battery_capacity: number;
  min_battery_capacity: number;
  completed: boolean;
}

interface FlightPathFeature {
  type: string;
  id: string;
  geometry: {
    type: string;
    coordinates: number[][];
  };
  properties: FlightPathProperties;
}

interface DroneFlightPathsLayerProps {
  wfsUrl: string;
  refreshInterval?: number; // in milliseconds
  visible?: boolean;
}

const DroneFlightPathsLayer = ({
  wfsUrl,
  refreshInterval = 30000, // Refresh every 30 seconds by default
  visible = true
}: DroneFlightPathsLayerProps) => {
  const map = useMap();
  const [flightPaths, setFlightPaths] = useState<FlightPathFeature[]>([]);
  const [pathLayers, setPathLayers] = useState<L.Polyline[]>([]);

  // Fetch flight path data from WFS
  const fetchFlightPaths = async () => {
    if (!visible) return;

    try {
      const response = await fetch(wfsUrl);
      const data = await response.json();

      if (data.features && Array.isArray(data.features)) {
        console.log(`Flight paths: ${data.features.length} paths loaded`);
        setFlightPaths(data.features);
      }
    } catch (error) {
      console.error('Error fetching flight path data:', error);
    }
  };

  // Initial fetch and set up polling
  useEffect(() => {
    if (!visible) {
      // Clear path layers when not visible
      pathLayers.forEach(layer => map.removeLayer(layer));
      setPathLayers([]);
      return;
    }

    fetchFlightPaths();
    const interval = setInterval(fetchFlightPaths, refreshInterval);

    return () => {
      clearInterval(interval);
    };
  }, [visible, refreshInterval]);

  // Update path layers when flight path data changes
  useEffect(() => {
    // Clear existing path layers
    pathLayers.forEach(layer => map.removeLayer(layer));

    if (!visible) {
      setPathLayers([]);
      return;
    }

    // Create new polyline layers
    const newPathLayers = flightPaths.map((path) => {
      const props = path.properties;

      // Convert coordinates from [lon, lat] to [lat, lon] for Leaflet
      const latLngs: [number, number][] = path.geometry.coordinates.map(
        coord => [coord[1], coord[0]]
      );

      // Color based on completion status
      const pathColor = props.completed ? '#6b7280' : '#3b82f6'; // gray for completed, blue for active
      const pathOpacity = props.completed ? 0.4 : 0.7;

      const polyline = L.polyline(latLngs, {
        color: pathColor,
        weight: 3,
        opacity: pathOpacity,
        smoothFactor: 1,
      });

      // Create detailed popup
      const startTime = new Date(props.start_time).toLocaleString();
      const endTime = new Date(props.end_time).toLocaleString();
      const duration = Math.round((new Date(props.end_time).getTime() - new Date(props.start_time).getTime()) / 60000);

      const popupContent = `
        <div style="min-width: 260px; font-family: system-ui;">
          <div style="margin-bottom: 12px; padding-bottom: 12px; border-bottom: 2px solid #e5e7eb;">
            <div style="font-size: 14px; font-weight: 600; color: #1f2937; margin-bottom: 4px;">
              ✈️ Flight Path
            </div>
            <div style="font-size: 11px; color: #6b7280; font-family: monospace;">
              ${props.flight_id}
            </div>
          </div>

          <div style="background: #f9fafb; padding: 10px; border-radius: 6px; font-size: 12px; margin-bottom: 10px;">
            <div style="margin-bottom: 6px;">
              <strong>Drone:</strong>
              <span style="font-family: monospace; color: #3b82f6;">${props.drone_id}</span>
            </div>
            <div style="margin-bottom: 6px;">
              <strong>Status:</strong>
              <span style="color: ${props.completed ? '#10b981' : '#f59e0b'}; font-weight: 600;">
                ${props.completed ? 'Completed ✓' : 'In Progress...'}
              </span>
            </div>
            <div style="margin-bottom: 6px;">
              <strong>Duration:</strong> ${duration} minutes
            </div>
          </div>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px;">
            <div style="background: #f3f4f6; padding: 8px; border-radius: 6px;">
              <div style="font-size: 10px; color: #6b7280; font-weight: 500;">DISTANCE</div>
              <div style="font-size: 14px; font-weight: 600; color: #1f2937;">${(props.flight_distance / 1000).toFixed(2)} km</div>
            </div>
            <div style="background: #f3f4f6; padding: 8px; border-radius: 6px;">
              <div style="font-size: 10px; color: #6b7280; font-weight: 500;">POINTS</div>
              <div style="font-size: 14px; font-weight: 600; color: #1f2937;">${props.points_count}</div>
            </div>
            <div style="background: #f3f4f6; padding: 8px; border-radius: 6px;">
              <div style="font-size: 10px; color: #6b7280; font-weight: 500;">MAX HEIGHT</div>
              <div style="font-size: 14px; font-weight: 600; color: #1f2937;">${Math.round(props.max_height)}m</div>
            </div>
            <div style="background: #f3f4f6; padding: 8px; border-radius: 6px;">
              <div style="font-size: 10px; color: #6b7280; font-weight: 500;">MAX SPEED</div>
              <div style="font-size: 14px; font-weight: 600; color: #1f2937;">${props.max_speed.toFixed(1)} m/s</div>
            </div>
          </div>

          <div style="background: #f9fafb; padding: 10px; border-radius: 6px; font-size: 11px;">
            <div style="margin-bottom: 4px;">
              <strong>Start:</strong> ${startTime}
            </div>
            <div style="margin-bottom: 4px;">
              <strong>End:</strong> ${endTime}
            </div>
            <div>
              <strong>Avg Battery:</strong> ${props.avg_battery_capacity.toFixed(1)}%
              (Min: ${props.min_battery_capacity}%)
            </div>
          </div>
        </div>
      `;

      polyline.bindPopup(popupContent);
      polyline.addTo(map);

      return polyline;
    });

    setPathLayers(newPathLayers);

    return () => {
      newPathLayers.forEach(layer => map.removeLayer(layer));
    };
  }, [flightPaths, map, visible]);

  return null;
};

export default DroneFlightPathsLayer;
